import { useEffect, useRef, useState } from "react";
import { apiGet, apiPut } from "@/shared/api";
import type { UploadedAttachment } from "./api";

/**
 * Bozza della risposta salvata sul server, una per conversazione.
 *
 * Davide 16/10/2026: «uscendo il messaggio si è cancellato» (testo del reso +
 * etichetta allegata). Sul server e non nel browser: la ritrova da qualunque
 * PC, e non sparisce se il browser pulisce i dati.
 *
 * - All'apertura della chat la rilegge e la rimette nel composer, ma solo
 *   dove il composer è ancora vuoto: la bozza del reso appena creato (che
 *   arriva da ``prepareResoDraft``) non viene mai schiacciata.
 * - Salva da sola dopo una pausa di scrittura (``RITARDO_MS``), e subito
 *   quando si cambia chat o si chiude la pagina.
 * - Composer svuotato (anche dopo l'invio) = bozza tolta. Il backend la toglie
 *   comunque all'invio.
 * - Finché la lettura iniziale non è tornata non si salva niente: un
 *   salvataggio a composer vuoto cancellerebbe la bozza che sta arrivando.
 */
export type Destinatario = "customer" | "operator" | "all";

export type BozzaLetta = {
  testo: string;
  destinatario: Destinatario;
  allegati: UploadedAttachment[];
  aggiornata_il: string;
};

export type StatoBozza = "salvata" | "errore" | null;

export const RITARDO_MS = 800;

type Contenuto = { testo: string; destinatario: Destinatario; allegati: number[] };

function vuoto(c: Contenuto): boolean {
  return !c.testo.trim() && c.allegati.length === 0;
}

function url(threadId: number): string {
  return `/api/mail/threads/${threadId}/bozza`;
}

function salva(threadId: number, c: Contenuto, keepalive = false): Promise<unknown> {
  return apiPut(url(threadId), c, keepalive ? { keepalive: true } : undefined);
}

interface Args {
  body: string;
  attachments: UploadedAttachment[];
  recipient: Destinatario;
  setBody: (f: (prev: string) => string) => void;
  setAttachments: (f: (prev: UploadedAttachment[]) => UploadedAttachment[]) => void;
  setRecipient: (r: Destinatario) => void;
}

export function useBozzaRisposta(
  threadId: number | null,
  { body, attachments, recipient, setBody, setAttachments, setRecipient }: Args,
): StatoBozza {
  const [stato, setStato] = useState<StatoBozza>(null);
  const [letta, setLetta] = useState(false);
  // Ultimo contenuto che il server conosce: niente PUT se non è cambiato, e un
  // composer vuoto non manda niente se sul server non c'è nulla da togliere.
  const salvatoRef = useRef<string>(
    JSON.stringify({ testo: "", destinatario: "customer", allegati: [] }),
  );
  const correnteRef = useRef<Contenuto>({ testo: "", destinatario: "customer", allegati: [] });
  const contenuto: Contenuto = {
    testo: body,
    destinatario: recipient,
    allegati: attachments.map((a) => a.id),
  };
  correnteRef.current = contenuto;
  const chiave = JSON.stringify(contenuto);

  // Lettura all'apertura della chat.
  useEffect(() => {
    if (threadId == null) return;
    let annullato = false;
    void apiGet<BozzaLetta | null>(url(threadId))
      .then((bozza) => {
        if (annullato || !bozza) return;
        salvatoRef.current = JSON.stringify({
          testo: bozza.testo,
          destinatario: bozza.destinatario,
          allegati: bozza.allegati.map((a) => a.id),
        });
        if (vuoto(correnteRef.current)) setRecipient(bozza.destinatario);
        setBody((prev) => (prev.trim() ? prev : bozza.testo));
        setAttachments((prev) => [
          ...prev,
          ...bozza.allegati.filter((a) => !prev.some((p) => p.id === a.id)),
        ]);
        setStato("salvata");
      })
      .catch(() => {
        if (!annullato) setStato("errore");
      })
      .finally(() => {
        if (!annullato) setLetta(true);
      });
    return () => {
      annullato = true;
    };
  }, [threadId, setBody, setAttachments, setRecipient]);

  // Salvataggio dopo una pausa di scrittura.
  useEffect(() => {
    if (threadId == null || !letta || chiave === salvatoRef.current) return;
    const timer = window.setTimeout(() => {
      const daSalvare = chiave;
      void salva(threadId, JSON.parse(daSalvare) as Contenuto)
        .then(() => {
          salvatoRef.current = daSalvare;
          setStato(vuoto(JSON.parse(daSalvare) as Contenuto) ? null : "salvata");
        })
        .catch(() => setStato("errore"));
    }, RITARDO_MS);
    return () => window.clearTimeout(timer);
  }, [threadId, letta, chiave]);

  // Uscita: cambio chat (il composer è montato per thread) o pagina chiusa.
  // Si manda solo ciò che il server non ha ancora.
  const lettaRef = useRef(letta);
  lettaRef.current = letta;
  useEffect(() => {
    if (threadId == null) return;
    const svuota = (keepalive: boolean) => {
      const ultimo = JSON.stringify(correnteRef.current);
      if (!lettaRef.current || ultimo === salvatoRef.current) return;
      salvatoRef.current = ultimo;
      void salva(threadId, correnteRef.current, keepalive).catch(() => undefined);
    };
    const allaChiusura = () => svuota(true);
    window.addEventListener("pagehide", allaChiusura);
    return () => {
      window.removeEventListener("pagehide", allaChiusura);
      svuota(true);
    };
  }, [threadId]);

  return stato;
}

/** Spia accanto ai tasti del composer: dice che la bozza è al sicuro. */
export function BozzaStato({ stato }: { stato: StatoBozza }) {
  if (stato === "salvata") return <span className="text-[12px] text-ink">Bozza salvata</span>;
  if (stato === "errore")
    return (
      <span className="text-[11px] text-red">Bozza non salvata: controlla la connessione</span>
    );
  return null;
}
